import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { CorporateEntityService } from '../app/api/services/CorporateEntityService';
import { withAuth, getAuthToken } from '../lib/auth';
import { supabase } from '../lib/supabase';
import { formatPhoneNumber } from '../lib/utils';
import { Button } from '../components/ui/button';
import '../lib/apiConfig'; // Initialize API config

function CompanyProfile() {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [company, setCompany] = useState(null);

    useEffect(() => {
        const loadCompany = async () => {
            try {
                // First, check if company data was passed via navigation state
                if (location.state?.company && location.state.company.id === parseInt(id, 10)) {
                    setCompany(location.state.company);
                    setLoading(false);
                    return;
                }

                // Check authentication first
                const { data: { session }, error: sessionError } = await supabase.auth.getSession();

                if (sessionError) {
                    console.error('Error getting session:', sessionError);
                }

                if (!session) {
                    setError('You must be logged in to view company profiles.');
                    setTimeout(() => navigate('/login'), 2000);
                    return;
                }

                // Double-check we have a token
                const token = await getAuthToken();
                if (!token) {
                    setError('You must be logged in to view company profiles.');
                    setTimeout(() => navigate('/login'), 2000);
                    return;
                }

                // Try to fetch company using the single company endpoint first
                let companyData = null;
                try {
                    const response = await withAuth(async () => {
                        return await CorporateEntityService.getApiV2CorporateEntity1(parseInt(id, 10));
                    });

                    // The API returns { status, message, data } where data contains the company
                    if (response?.data) {
                        companyData = response.data;
                    }
                } catch (singleEndpointError) {
                    // If single endpoint fails, fall back to list endpoint
                    console.log('Single company endpoint failed, trying list endpoint:', singleEndpointError);
                    
                    const response = await withAuth(async () => {
                        return await CorporateEntityService.getApiV2CorporateEntity(
                            undefined, // search
                            undefined, // industry
                            undefined, // size
                            undefined, // managerid
                            true, // activeflag - only show active companies
                            'ONE', // publishStatus - only show published companies
                            undefined, // moq
                            undefined, // productOrService
                            undefined, // certificate
                            undefined, // country
                            undefined, // page
                            undefined, // pageSize
                            undefined, // limit
                            undefined, // offset
                            undefined, // sort
                            undefined // order
                        );
                    });

                    // Find the company with matching ID
                    const companies = response.data || [];
                    companyData = companies.find((c) => c.id === parseInt(id, 10));
                }

                if (companyData) {
                    setCompany(companyData);
                } else {
                    setError('Company not found.');
                }
            } catch (error) {
                console.error('Error loading company:', error);
                setError('Failed to load company profile. Please try again.');
                if (error?.status === 401) {
                    setTimeout(() => navigate('/login'), 2000);
                } else if (error?.status === 404) {
                    setError('Company not found.');
                }
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            loadCompany();
        } else {
            setError('Invalid company ID.');
            setLoading(false);
        }
    }, [id, navigate, location.state]);

    if (loading) {
        return (
            <main className="App-main">
                <div className="flex justify-center items-center min-h-[60vh] flex-col gap-4">
                    <div className="text-xl text-muted-foreground">Loading company profile...</div>
                </div>
            </main>
        );
    }

    if (error && !company) {
        return (
            <main className="App-main">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="mb-6">
                        <Button
                            onClick={() => navigate('/directory')}
                            variant="outline"
                            className="mb-4"
                        >
                            ← Back to Directory
                        </Button>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-red-700">
                        {error}
                    </div>
                </div>
            </main>
        );
    }

    if (!company) {
        return null;
    }

    // Collect all unique products and services from all locations
    const allProducts = [];
    const allServices = [];
    const allCertifications = [];

    if (company.location) {
        company.location.forEach((location) => {
            // Collect products
            if (location.locationProducts) {
                location.locationProducts.forEach((lp) => {
                    if (lp.product && !allProducts.find((p) => p.id === lp.product.id)) {
                        allProducts.push({
                            ...lp.product,
                            buying: lp.buying,
                            locationId: location.id,
                            locationName: location.city || location.address || 'Unknown Location',
                        });
                    }
                });
            }

            // Collect services
            if (location.locationServices) {
                location.locationServices.forEach((ls) => {
                    if (ls.service && !allServices.find((s) => s.id === ls.service.id)) {
                        allServices.push({
                            ...ls.service,
                            buying: ls.buying,
                            locationId: location.id,
                            locationName: location.city || location.address || 'Unknown Location',
                        });
                    }
                });
            }

            // Collect certifications
            if (location.locationcertifications) {
                location.locationcertifications.forEach((lc) => {
                    if (lc.certification) {
                        allCertifications.push({
                            ...lc.certification,
                            year: lc.yearcertified,
                            locationId: location.id,
                            locationName: location.city || location.address || 'Unknown Location',
                        });
                    }
                });
            }
        });
    }

    return (
        <main className="App-main">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Back Button */}
                <div className="mb-6">
                    <Button
                        onClick={() => navigate('/directory')}
                        variant="outline"
                    >
                        ← Back to Directory
                    </Button>
                </div>

                {/* Company Header */}
                <div className="bg-card rounded-lg shadow-sm border border-border overflow-hidden mb-6">
                    <div className="px-6 py-8 bg-gradient-to-r from-brand-orange-muted to-brand-purple/10 border-b border-border">
                        <h1 className="text-4xl font-bold text-foreground mb-4">
                            {company.legalname || 'Unnamed Company'}
                        </h1>
                        {company.description && (
                            <p className="text-lg text-muted-foreground leading-relaxed">
                                {company.description}
                            </p>
                        )}
                    </div>

                    {/* Company Overview */}
                    <div className="px-6 py-6">
                        <h2 className="text-2xl font-semibold text-foreground mb-6">Company Overview</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {company.website && (
                                <div className="flex items-start">
                                    <span className="text-muted-foreground mr-3 text-xl">🌐</span>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                                            Website
                                        </div>
                                        <a
                                            href={company.website}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-brand-orange hover:underline break-all text-sm"
                                        >
                                            {company.website}
                                        </a>
                                    </div>
                                </div>
                            )}

                            {company.phone && (
                                <div className="flex items-start">
                                    <span className="text-muted-foreground mr-3 text-xl">📞</span>
                                    <div className="flex-1">
                                        <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                                            Phone
                                        </div>
                                        <div className="text-foreground text-sm">{formatPhoneNumber(company.phone)}</div>
                                    </div>
                                </div>
                            )}

                            {company.industry && (
                                <div className="flex items-start">
                                    <span className="text-muted-foreground mr-3 text-xl">🏭</span>
                                    <div className="flex-1">
                                        <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                                            Industry
                                        </div>
                                        <div className="text-foreground text-sm">{company.industry}</div>
                                        {company.industries?.indname && (
                                            <div className="text-xs text-muted-foreground mt-1">
                                                {company.industries.indname}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {company.size && (
                                <div className="flex items-start">
                                    <span className="text-muted-foreground mr-3 text-xl">🏢</span>
                                    <div className="flex-1">
                                        <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                                            Company Size
                                        </div>
                                        <div className="text-foreground text-sm">{company.size}</div>
                                    </div>
                                </div>
                            )}

                            {company.MOQ && (
                                <div className="flex items-start">
                                    <span className="text-muted-foreground mr-3 text-xl">📦</span>
                                    <div className="flex-1">
                                        <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                                            Minimum Order Quantity
                                        </div>
                                        <div className="text-foreground text-sm">{company.MOQ}</div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Locations Section */}
                {company.location && company.location.length > 0 && (
                    <div className="bg-card rounded-lg shadow-sm border border-border overflow-hidden mb-6">
                        <div className="px-6 py-5 bg-gradient-to-r from-brand-orange-muted to-brand-purple/10 border-b border-border">
                            <h2 className="text-2xl font-semibold text-foreground flex items-center">
                                <span className="mr-3">📍</span>
                                Locations ({company.location.length})
                            </h2>
                        </div>
                        <div className="px-6 py-6">
                            <div className="space-y-6">
                                {company.location.map((location) => (
                                    <div
                                        key={location.id}
                                        className="p-5 bg-muted rounded-lg border border-border"
                                    >
                                        <h3 className="text-lg font-semibold text-foreground mb-4">
                                            {location.city && location.country_code
                                                ? `${location.city}, ${location.country_code}`
                                                : location.address || `Location #${location.id}`}
                                        </h3>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                                            {location.address && (
                                                <div>
                                                    <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                                                        Address
                                                    </div>
                                                    <div className="text-foreground">{location.address}</div>
                                                </div>
                                            )}
                                            {location.city && (
                                                <div>
                                                    <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                                                        City
                                                    </div>
                                                    <div className="text-foreground">{location.city}</div>
                                                </div>
                                            )}
                                            {location.state && (
                                                <div>
                                                    <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                                                        State/Province
                                                    </div>
                                                    <div className="text-foreground">{location.state}</div>
                                                </div>
                                            )}
                                            {location.postal_code && (
                                                <div>
                                                    <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                                                        Postal Code
                                                    </div>
                                                    <div className="text-foreground">{location.postal_code}</div>
                                                </div>
                                            )}
                                            {location.country_code && (
                                                <div>
                                                    <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                                                        Country
                                                    </div>
                                                    <div className="text-foreground">{location.country_code}</div>
                                                </div>
                                            )}
                                            {location.businessfunction && (
                                                <div>
                                                    <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                                                        Business Function
                                                    </div>
                                                    <div className="text-foreground">{location.businessfunction}</div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Location-specific Products */}
                                        {location.locationProducts && location.locationProducts.length > 0 && (
                                            <div className="mt-4 pt-4 border-t border-border">
                                                <div className="text-xs text-muted-foreground uppercase tracking-wide mb-2">
                                                    Products
                                                </div>
                                                <div className="flex flex-wrap gap-2">
                                                    {location.locationProducts.map((lp, idx) => (
                                                        <span
                                                            key={idx}
                                                            className="inline-block px-3 py-1 text-xs bg-background text-foreground rounded border border-border"
                                                        >
                                                            {lp.product?.itemname || lp.product?.categoryname || 'Product'}
                                                            {lp.buying && (
                                                                <span className="ml-2 text-muted-foreground">(Buying)</span>
                                                            )}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Location-specific Services */}
                                        {location.locationServices && location.locationServices.length > 0 && (
                                            <div className="mt-4 pt-4 border-t border-border">
                                                <div className="text-xs text-muted-foreground uppercase tracking-wide mb-2">
                                                    Services
                                                </div>
                                                <div className="flex flex-wrap gap-2">
                                                    {location.locationServices.map((ls, idx) => (
                                                        <span
                                                            key={idx}
                                                            className="inline-block px-3 py-1 text-xs bg-background text-foreground rounded border border-border"
                                                        >
                                                            {ls.service?.itemname || ls.service?.categoryname || 'Service'}
                                                            {ls.buying && (
                                                                <span className="ml-2 text-muted-foreground">(Buying)</span>
                                                            )}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Location-specific Certifications */}
                                        {location.locationcertifications && location.locationcertifications.length > 0 && (
                                            <div className="mt-4 pt-4 border-t border-border">
                                                <div className="text-xs text-muted-foreground uppercase tracking-wide mb-2">
                                                    Certifications
                                                </div>
                                                <div className="flex flex-wrap gap-2">
                                                    {location.locationcertifications.map((lc, idx) => (
                                                        <span
                                                            key={idx}
                                                            className="inline-block px-3 py-1 text-xs bg-background text-foreground rounded border border-border"
                                                        >
                                                            {lc.certification?.certname || 'Certification'}
                                                            {lc.yearcertified && (
                                                                <span className="ml-2 text-muted-foreground">
                                                                    ({lc.yearcertified})
                                                                </span>
                                                            )}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Products Section (All Products) */}
                {allProducts.length > 0 && (
                    <div className="bg-card rounded-lg shadow-sm border border-border overflow-hidden mb-6">
                        <div className="px-6 py-5 bg-gradient-to-r from-brand-orange-muted to-brand-purple/10 border-b border-border">
                            <h2 className="text-2xl font-semibold text-foreground flex items-center">
                                <span className="mr-3">📦</span>
                                Products ({allProducts.length})
                            </h2>
                        </div>
                        <div className="px-6 py-6">
                            <div className="flex flex-wrap gap-3">
                                {allProducts.map((product, idx) => (
                                    <div
                                        key={idx}
                                        className="px-4 py-2 bg-muted rounded-lg border border-border"
                                    >
                                        <div className="font-medium text-foreground">
                                            {product.itemname || product.categoryname || 'Product'}
                                        </div>
                                        {product.buying && (
                                            <div className="text-xs text-muted-foreground mt-1">Available for purchase</div>
                                        )}
                                        <div className="text-xs text-muted-foreground mt-1">
                                            Location: {product.locationName}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Services Section (All Services) */}
                {allServices.length > 0 && (
                    <div className="bg-card rounded-lg shadow-sm border border-border overflow-hidden mb-6">
                        <div className="px-6 py-5 bg-gradient-to-r from-brand-orange-muted to-brand-purple/10 border-b border-border">
                            <h2 className="text-2xl font-semibold text-foreground flex items-center">
                                <span className="mr-3">🔧</span>
                                Services ({allServices.length})
                            </h2>
                        </div>
                        <div className="px-6 py-6">
                            <div className="flex flex-wrap gap-3">
                                {allServices.map((service, idx) => (
                                    <div
                                        key={idx}
                                        className="px-4 py-2 bg-muted rounded-lg border border-border"
                                    >
                                        <div className="font-medium text-foreground">
                                            {service.itemname || service.categoryname || 'Service'}
                                        </div>
                                        {service.buying && (
                                            <div className="text-xs text-muted-foreground mt-1">Available for purchase</div>
                                        )}
                                        <div className="text-xs text-muted-foreground mt-1">
                                            Location: {service.locationName}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Certifications Section (All Certifications) */}
                {allCertifications.length > 0 && (
                    <div className="bg-card rounded-lg shadow-sm border border-border overflow-hidden mb-6">
                        <div className="px-6 py-5 bg-gradient-to-r from-brand-orange-muted to-brand-purple/10 border-b border-border">
                            <h2 className="text-2xl font-semibold text-foreground flex items-center">
                                <span className="mr-3">🏆</span>
                                Certifications ({allCertifications.length})
                            </h2>
                        </div>
                        <div className="px-6 py-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {allCertifications.map((cert, idx) => (
                                    <div
                                        key={idx}
                                        className="p-4 bg-muted rounded-lg border border-border"
                                    >
                                        <div className="font-medium text-foreground mb-2">
                                            {cert.certname || 'Certification'}
                                        </div>
                                        {cert.certcategory && (
                                            <div className="text-sm text-muted-foreground mb-1">
                                                Category: {cert.certcategory}
                                            </div>
                                        )}
                                        {cert.tier && (
                                            <div className="text-sm text-muted-foreground mb-1">
                                                Tier: {cert.tier}
                                            </div>
                                        )}
                                        {cert.year && (
                                            <div className="text-sm text-muted-foreground mb-1">
                                                Year: {cert.year}
                                            </div>
                                        )}
                                        {cert.verified && (
                                            <div className="text-xs text-green-600 mt-2">✓ Verified</div>
                                        )}
                                        <div className="text-xs text-muted-foreground mt-2">
                                            Location: {cert.locationName}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </main>
    );
}

export default CompanyProfile;

